import { configureDraftScope } from "./drafts";

/**
 * Per-vault localStorage isolation.
 *
 * `localStorage` is scoped per browser origin, NOT per vault. Without
 * coordination, state that conceptually belongs to a specific vault
 * (recent searches, expanded tree nodes, selected KB filter, graph
 * scope with entity slugs from THAT vault) leaks across vault changes.
 *
 * The user's complaint that triggered this module: after switching from
 * vault A to vault B, the wiki sidebar still showed search history from
 * vault A. Same class of leak: tree-node expansions, active KB filter,
 * graph-scope entity slugs, command-palette MRU article slugs — all
 * displayed stale references that don't apply to the new vault.
 *
 * This module:
 *   1. Lists every localStorage key that holds VAULT-specific state.
 *   2. Detects vault change by comparing the current `/api/sync.external_path`
 *      against the stored marker.
 *   3. Clears all per-vault keys when the vault has changed.
 *   4. Updates the marker so the next change is detected.
 *
 * What stays per-origin (NOT cleared — these are user preferences, not
 * per-vault data): theme, language, sidebar width, TTS preferences,
 * autosend, settings active section, list/gallery toggles, hide-digests
 * toggles, docs view mode, mode-pill selection (Simple / More / Files),
 * folded sidebar groups, favorites (they are addresses in the app, not
 * vault content), etc.
 */

const VAULT_MARKER_KEY = "agnt-active-vault-path";

/**
 * Per-vault localStorage keys — cleared on vault change.
 * Add new keys here when you introduce per-vault state.
 */
const PER_VAULT_KEYS: readonly string[] = [
	// Wiki page
	"wiki-recent-searches",     // user's search queries — could leak topical info
	"wiki-expanded-nodes",      // expanded tree node ids (KB / project / taxonomy slugs from old vault)
	"wiki-active-kb",           // selected KB filter (slug from old vault)
	"wiki-active-project",      // selected project filter (slug from old vault)

	// Wiki command palette
	"wiki-palette-mru",         // most-recently-used article slugs

	// Graph page — entity slugs are per-vault content
	"agnt-graph-scope-v2",
	"agnt-graph-scope-collapse",
] as const;

/**
 * Per-vault sessionStorage keys — cleared on vault change.
 *
 * sessionStorage SURVIVES page reloads (only cleared on tab close), and
 * the vault-change flow re-execs the server + page reload in the same
 * tab. So a session-scoped flag set against vault A persists into
 * vault B unless we explicitly clear it.
 *
 * The canonical leak: ``agnt-onboard-checked`` short-circuits the
 * onboarding redirect — after switching from an onboarded vault to a
 * fresh-needs-onboarding vault, the user would be dropped on the
 * dashboard instead of routed to ``/onboarding``.
 */
const PER_VAULT_SESSION_KEYS: readonly string[] = [
	"agnt-onboard-checked",     // onboarding-check short-circuit; per-vault state
] as const;

/**
 * Check whether the active vault has changed since the last page load.
 * If so, wipe all per-vault localStorage keys so stale state from the
 * previous vault doesn't bleed into the new one.
 *
 * Idempotent on re-runs against the same vault — only clears when the
 * vault path actually differs from the stored marker.
 *
 * Network failure is conservative: if `/api/sync` is unreachable, we do
 * NOT clear (could be a transient blip; clearing would lose work).
 *
 * Called once at app boot from `main.tsx` BEFORE any per-vault React
 * state initializes, so first paint reads from a clean store.
 */
export async function clearStaleVaultStateIfChanged(): Promise<void> {
	configureDraftScope(null);
	try {
		const res = await fetch("/api/sync", { cache: "no-store" });
		if (!res.ok) return;
		const data = await res.json();
		const currentPath: string =
			(data && (data.external_path as string)) ||
			(data && (data.vault_path as string)) ||
			"";
		if (!currentPath || typeof currentPath !== "string") return;
		configureDraftScope(currentPath);

		const stored = localStorage.getItem(VAULT_MARKER_KEY);
		if (stored && stored !== currentPath) {
			// Vault changed — clear per-vault state in BOTH stores.
			for (const key of PER_VAULT_KEYS) {
				try {
					localStorage.removeItem(key);
				} catch {
					/* quota or permission — best-effort */
				}
			}
			for (const key of PER_VAULT_SESSION_KEYS) {
				try {
					sessionStorage.removeItem(key);
				} catch {
					/* best-effort */
				}
			}
		}
		// Always update the marker so the next change is detected.
		// On first load (stored === null), this is the initial seed.
		localStorage.setItem(VAULT_MARKER_KEY, currentPath);
	} catch {
		// Network unreachable — leave state alone to avoid clearing on a
		// transient outage.
	}
}
